import React, { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Col, Divider, Row, Modal, Avatar, Tooltip, Form, Input, Button, Spin } from "antd";
import { Calendar } from 'iconsax-react';
import { LogoutOutlined, UserSwitchOutlined, LockOutlined } from '@ant-design/icons';
import { useSelector } from "react-redux";

import store from "../../../redux/store";
import * as actions from "../../../redux/login/loginTypes";
import { UserLogout } from "../../../redux/login/loginActions";
import { getUserData, saveUser } from "../../../redux/settings/settingsActions";
import UserCustomerModal from "../../../view/userCustomerModal";
import { uniqueValues } from "../../../utils/utils";

export default function HeaderUser() {
  const [isCustomerModalVisible, setIsCustomerModalVisible] = useState(false);
  const [customerListState, setCustomerListState] = useState([]);
  const [openingViaLogin, setOpeningViaLogin] = useState(false);
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [userData, setUserData] = useState(null);

  const [changePasswordForm] = Form.useForm();

  const { userCustomerList } = useSelector((state) => state.login);

  useEffect(() => {
    const uniqueCustomerList = uniqueValues(userCustomerList, "cardCode", "cardName", "concatenate");
    const sortedCustomerList = uniqueCustomerList?.sort((a, b) => a.props.children.localeCompare(b.props.children));

    setCustomerListState(sortedCustomerList);
  }, [userCustomerList]);

  const handleLogout = async () => {
    store.dispatch({ type: actions.LOGOUT });
    UserLogout();
  };

  const handleSelectCustomer = () => {
    setIsCustomerModalVisible(true);
    setOpeningViaLogin(false);
  };

  const handleCustomerCancel = () => {
    setIsCustomerModalVisible(false);
  };

  const handleCustomerOk = () => {
    setIsCustomerModalVisible(false);
  };

  const handleShowLogoutModal = () => {
    Modal.confirm({
      title: 'Logout',
      content: 'Are you sure you want to logout?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: handleLogout
    });
  };

  const handleSpaceKeyDown = (event) => {
    if (event.key === ' ') {
      event.preventDefault();
    }
  };

  const handleOpenChangePasswordModal = async () => {
    setChangePasswordLoading(true);
    setIsChangePasswordModalVisible(true);
    const response = await getUserData({ user: localStorage.getItem("user") });
    setChangePasswordLoading(false);
    if (response) {
      setUserData(response[0]);
      changePasswordForm.setFieldsValue({ currentPassword: response[0].password });
    }
  };

  const handleCloseChangePasswordModal = () => {
    setIsChangePasswordModalVisible(false);
    changePasswordForm.resetFields();
    setUserData(null);
  };

  const handleChangePassword = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      return Modal.error({ title: 'Error', content: "Passwords do not match." });
    }

    if (values.newPassword === userData?.password) {
      return Modal.error({ title: 'Error', content: "New password cannot be the same as the current password." });
    }

    const formData = new FormData();
    formData.append('id', userData?.id);
    formData.append('cardCode', userData?.cardCode);
    formData.append('cardName', userData?.cardName);
    formData.append('mailId', userData?.mailId);
    formData.append('userName', userData?.userName);
    formData.append('password', values.newPassword);
    formData.append('role', userData?.role);
    formData.append('active', userData?.active);

    setChangePasswordLoading(true);
    const response = await saveUser(formData);
    setChangePasswordLoading(false);

    if (response.result) {
      Modal.success({ title: "Password changed successfully!" });
      handleCloseChangePasswordModal();
    } else {
      Modal.error({ title: 'Error', content: response?.msg || "Failed to change password." });
    }
  };

  const menu = (
    <div className="hp-user-dropdown hp-border-radius hp-bg-black-0 hp-bg-dark-100 hp-border-color-dark-80 hp-py-24 hp-px-18 hp-mt-16">
      <span className="hp-d-block h5 hp-font-weight-500 hp-text-color-black-100 hp-text-color-dark-0 hp-mb-16">Profile Settings</span>
      {userCustomerList && userCustomerList?.length > 1 && (
        <>
          <Divider className="hp-mt-18 hp-mb-12" />
          <Row>
            <Col span={24}>
              <Link to="#" className="hp-d-flex-center hp-p1-body hp-font-weight-500 hp-my-4 hp-py-8 hp-px-10 hp-d-block hp-transition hp-hover-bg-primary-4 hp-hover-bg-dark-80 hp-border-radius" style={{ marginTop: -7, marginLeft: -10, marginRight: -10 }}>
                <UserSwitchOutlined size="20" /><span className="hp-ml-8" onClick={handleSelectCustomer}>Switch Customer</span>
              </Link>
            </Col>
          </Row>
        </>
      )}
      <Divider className="hp-mb-18 hp-mt-12" />
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Link to="#" className="hp-d-flex-center hp-p1-body hp-font-weight-500 hp-my-4 hp-py-8 hp-px-10 hp-d-block hp-transition hp-hover-bg-primary-4 hp-hover-bg-dark-80 hp-border-radius" style={{ marginTop: -7, marginLeft: -10, marginRight: -10 }}>
            <LockOutlined size="20" /><span className="hp-ml-8" onClick={handleOpenChangePasswordModal}>Change Password</span>
          </Link>
        </Col>
        <Col span={24}>
          <span className="hp-p1-body hp-font-weight-500 hp-hover-text-color-primary-2 hp-cursor-pointer" onClick={handleShowLogoutModal}>Log out</span>
        </Col>
      </Row>
    </div>
  );

  const renderuserAvatar = useCallback(() => {
    const userName = localStorage.getItem('userName');
    if (userName) {
      const splittedWords = userName?.split(' ');
      const userAvatar = splittedWords[0]?.charAt(0)?.toLocaleUpperCase();
      return userAvatar;
    }
    else {
      return "";
    };
  }, []);

  return (
    <>
      <Col className="hp-ml-18 hp-d-flex-center">
        <Dropdown overlay={menu} trigger={['click']} placement="bottomLeft">
          <Tooltip title='Profile Settings' placement='bottomLeft' >
            <div className="custom-user-profile-avatar">
              <div className="hp-border-radius-lg hp-overflow-hidden hp-bg-info-4 hp-m-4 hp-d-flex" style={{ minWidth: 24, width: 24, height: 24 }}>
                <Avatar shape="circle" size={24} style={{ backgroundColor: "#fff" }}>
                  <b style={{ color: "#424376", fontSize: "16px", fontWeight: 500 }}>{renderuserAvatar()}</b>
                </Avatar>
              </div>
            </div>
          </Tooltip>
        </Dropdown>
      </Col>

      <UserCustomerModal
        openingViaLogin={openingViaLogin}
        isVisible={isCustomerModalVisible}
        onOk={handleCustomerOk}
        onCancel={handleCustomerCancel}
        customerListState={customerListState}
        userCustomerList={userCustomerList}
      />

      <Modal
        title="Change Password"
        visible={isChangePasswordModalVisible}
        onCancel={handleCloseChangePasswordModal}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={changePasswordLoading}>
          <Form
            form={changePasswordForm}
            layout="vertical"
            onFinish={handleChangePassword}
          >
            <Form.Item
              name="currentPassword"
              label="Current Password"
            >
              <Input readOnly />
            </Form.Item>
            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[{ required: true, message: "Please enter a new password", whitespace: true }]}
            >
              <Input.Password onKeyDown={handleSpaceKeyDown} placeholder="Enter new password" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              rules={[{ required: true, message: "Please confirm your new password", whitespace: true }]}
            >
              <Input.Password onKeyDown={handleSpaceKeyDown} placeholder="Confirm new password" />
            </Form.Item>
            <Form.Item className="hp-mb-0">
              <Row justify="end" gutter={[12, 0]}>
                <Col>
                  <Button onClick={handleCloseChangePasswordModal}>Cancel</Button>
                </Col>
                <Col>
                  <Button type="primary" htmlType="submit" loading={changePasswordLoading}>
                    Change Password
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};
